import { useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";
import { api } from "../../lib/api";

/**
 * Native PayPal Smart Button — separate from Stripe. Talks to our own
 * `/api/paypal/orders` endpoints so the order + fulfillment + confirmation
 * email all fire through the same downstream flow.
 */
export const PayPalCheckout = ({ email, items, disabled, onSuccess }) => {
  const { clear } = useCart();
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  const providerOptions = useMemo(
    () => ({
      "client-id": clientId,
      currency: "USD",
      intent: "capture",
      // Explicitly disable other funding sources so this button is *only* PayPal.
      "disable-funding": "card,credit,paylater",
    }),
    [clientId]
  );

  if (!clientId) return null;

  const guardEmail = () => {
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email for your receipt");
      throw new Error("email-required");
    }
  };

  return (
    <div data-testid="paypal-button-wrapper" className="min-h-[52px]">
      <PayPalScriptProvider options={providerOptions}>
        <PayPalButtons
          disabled={disabled}
          style={{
            layout: "horizontal",
            color: "gold",
            shape: "rect",
            label: "paypal",
            height: 48,
            tagline: false,
          }}
          fundingSource={undefined}
          createOrder={async () => {
            guardEmail();
            const { data } = await api.post("/paypal/orders", {
              email,
              origin_url: window.location.origin,
              items: items.map((i) => ({
                product_id: i.product_id,
                size: i.size,
                color: i.color,
                color_key: i.color_key,
                image_path: i.image_path,
                qty: i.qty,
                design_id: i.design_id,
              })),
            });
            return data.paypal_order_id;
          }}
          onApprove={async (approve) => {
            try {
              const { data } = await api.post(
                `/paypal/orders/${approve.orderID}/capture`
              );
              toast.success("Order confirmed — receipt on its way");
              clear();
              onSuccess?.(data);
              // Route to the shared success page using the paypal-<id> session key
              window.location.href = `/payment/success?session_id=paypal-${approve.orderID}`;
            } catch (err) {
              const detail =
                err?.response?.data?.detail || "PayPal capture failed";
              toast.error(detail);
            }
          }}
          onError={(err) => {
            console.error("PayPal error:", err);
            const msg = err?.message || String(err) || "unknown";
            toast.error(`PayPal error: ${msg.slice(0, 140)}`);
          }}
          onCancel={() => toast.info("PayPal payment cancelled")}
        />
      </PayPalScriptProvider>
    </div>
  );
};

export default PayPalCheckout;
